import { MISSING_ITEMS_CATALOG } from '../../catalog/missingitems';
import { NOT_VALID_FOR_STRUCURE_CATALOG } from '../../catalog/notvalidforstructure';
import { UNCATEGORIZED_CATALOG } from '../../catalog/uncategorized';
import { formatItemNo } from '../../data/utils';
import { ITEM_TYPE_ART } from '../../types/itemTypes';
import { NODE_TYPE_ITEM } from '../../types/nodeTypes';
import { MISSING_ITEM } from '../../../store/constants/importitems';
import type { ItemDTO } from '../../../store/mappers/dtoMappers';
import { createId } from '../treeNode';
import type z from 'zod';
import type { UncategorizedItemsSchema } from '../../../schemas/items';

export type ItemNode = ReturnType<typeof createItemNode>;

export function countrySpecificToItemNodes({
  catalogId,
  items = [],
  level,
  parentId,
  isLeaf,
  inDraft,
  isInSuggestion,
}: {
  catalogId: string;
  items: {
    itemNo: string;
    itemType: string;
    itemNameOfficial: string;
    itemName?: string;
  }[];
  level: number;
  parentId: string;
  isLeaf: boolean;
  isInSuggestion?: boolean;
  inDraft?: boolean;
}) {
  if (!items) return [];

  const keyAndId = (item: { itemNo: string; itemType: string }) =>
    createId({
      parentId,
      type: NODE_TYPE_ITEM,
      contentId: `${item.itemNo}-${item.itemType}`,
    });

  return items.map((item) => ({
    id: keyAndId(item),
    key: keyAndId(item),
    itemType: item.itemType,
    itemNoFormated: formatItemNo(item.itemNo),
    itemNo: item.itemNo,
    title: item.itemName ?? MISSING_ITEM,
    role: 'treeitem',
    type: 'item',
    item,
    children: [],
    expanded: false,
    isLeaf,
    isChildItem: true,
    level,
    parentId,
    catalogId,
    isUncategorized: [
      UNCATEGORIZED_CATALOG.catalogId,
      MISSING_ITEMS_CATALOG.catalogId,
    ].includes(catalogId),
    inDraft,
    isInSuggestion,
  }));
}

export function toItemNodes({
  catalogId,
  items = [],
  level,
  parentId,
  isLeaf,
  inDraft,
  isInSuggestion,
}: {
  catalogId: string;
  items?: ItemDTO[];
  level: number;
  parentId: string;
  isLeaf?: boolean;
  isInSuggestion?: boolean;
  inDraft?: boolean;
}) {
  if (!items) return [];

  return items.map((item) =>
    createItemNode({
      catalogId,
      isLeaf,
      item,
      level,
      parentId,
      inDraft,
      isInSuggestion,
    }),
  );
}

export function toNotValidForStructureNodes(parentId: string, items: ItemDTO[]) {
  return items.map((item) =>
    createItemNode({
      catalogId: NOT_VALID_FOR_STRUCURE_CATALOG.catalogId,
      item,
      level: 1,
      parentId,
    }),
  );
}

const generateId = (
  parentId: string,
  item: { itemNo: string; itemType?: string | null },
) =>
  createId({
    parentId,
    type: NODE_TYPE_ITEM,
    contentId: `${item.itemNo}-${item.itemType}`,
  });

export function toUncategorizedItemNodes(
  parentId: string,
  level: number,
  items: z.infer<typeof UncategorizedItemsSchema>,
) {
  const uncategorizedNode = items.map((m) => ({
    catalogId: UNCATEGORIZED_CATALOG.catalogId,
    children: [],
    id: generateId(parentId, m),
    isLeaf: m.countrySpecificItemCount === 0,
    inDraft: m.inDraft,
    isInSuggestion: level > 1,
    isUncategorized: true,
    item: { ...m },
    itemNo: m.itemNo,
    itemNoFormated: formatItemNo(m.itemNo),
    itemType: m.itemType,
    key: generateId(parentId, m),
    level: level,
    parentId: parentId,
    role: 'treeitem',
    title: m.itemName,
    type: 'item',
  }));

  return uncategorizedNode;
}

const toItemChildrenNode = ({
  items,
  catalogId,
  isLeaf,
  level,
  parentId,
  inDraft,
  isInSuggestion,
  isUncategorized,
}: {
  items: {
    itemName: string;
    itemNo: string;
    itemType: string;
    itemNameOfficial: string;
    mainParent?: string | null | undefined;
    isLeaf?: boolean;
    isUncategorized?: boolean;
  }[];
  catalogId: string;
  isLeaf: boolean;
  level: number;
  parentId: string;
  isInSuggestion?: boolean;
  inDraft?: boolean;
  isUncategorized?: boolean;
}) =>
  items.map((item) => ({
    id: generateId(parentId, item),
    key: generateId(parentId, item),
    itemType: item.itemType,
    itemNoFormated: formatItemNo(item.itemNo),
    itemNo: item.itemNo,
    title: item.itemName ?? MISSING_ITEM,
    role: 'treeitem',
    type: 'item',
    item,
    children: [],
    expanded: false,
    isLeaf,
    isChildItem: true,
    level,
    parentId,
    catalogId,
    isUncategorized,
    inDraft,
    isInSuggestion,
  }));

function createItemNode({
  catalogId,
  inDraft,
  isLeaf,
  isInSuggestion,
  item,
  level,
  parentId,
}: {
  catalogId: string;
  item: ItemDTO;
  level: number;
  parentId: string;
  isLeaf?: boolean;
  isInSuggestion?: boolean;
  inDraft?: boolean;
}) {
  const { itemName, itemNo, itemType, itemCountrySpecificList = [] } = item;

  const leaf = isLeaf ?? (ITEM_TYPE_ART && !item.countrySpecificItemCount);
  const countrySpecificItems =
    itemCountrySpecificList
      ?.filter((f) => !!f.itemNameOfficial)
      .map((m) => ({
        ...m,
        itemName: m.itemNameOfficial,
        mainParent: item.mainParent,
        isLeaf: true,
        isUncategorized: false,
      })) || [];

  const isUncategorized = [
    UNCATEGORIZED_CATALOG.catalogId,
    MISSING_ITEMS_CATALOG.catalogId,
  ].includes(catalogId);

  return {
    catalogId,
    id: generateId(parentId, item),
    key: generateId(parentId, item),
    allowChildItems: true,
    item,
    itemNo,
    itemNoFormated: formatItemNo(itemNo),
    itemType,
    type: 'item',
    title: itemName ?? MISSING_ITEM,
    level,
    role: 'treeitem',
    isInSuggestion,
    inDraft,
    isLeaf: leaf,
    parentId,
    isUncategorized,
    children: toItemChildrenNode({
      catalogId,
      isLeaf: true,
      items: countrySpecificItems,
      level: level + 1,
      parentId,
      inDraft,
      isUncategorized,
    }),
  };
}
